import CLASS_TOKEN_MAP from '../../../theme/classTokenMap.json';

/**
 * The preview inspector: given an element on screen, which theme control moves it.
 *
 * The evidence is the class name, not the rendered colour. Reading
 * getComputedStyle back and matching it against the palette looks more thorough
 * and is worse: a dozen tokens resolve to the same grey, so it would answer
 * confidently and often wrongly. `bg-cmp-table-header` is the component itself
 * saying which token it reads - there is nothing to infer.
 *
 * The honest answer is sometimes "no control does". A raw ramp class like
 * `bg-gray-100` follows the grey ramp and has no semantic slot, which is exactly
 * what someone needs to know when a surface refuses to change: the surface is not
 * tokenised, and no amount of dragging will move it.
 */

const MAP = CLASS_TOKEN_MAP as Record<string, string>;

/**
 * Utility prefixes that take a colour, longest first so `border-t` is tried
 * before `border` and does not get parsed as scale `t-gray-200`.
 */
const UTILITIES: { prefix: string; property: string }[] = [
  { prefix: 'border-t', property: 'Border' },
  { prefix: 'border-r', property: 'Border' },
  { prefix: 'border-b', property: 'Border' },
  { prefix: 'border-l', property: 'Border' },
  { prefix: 'border-x', property: 'Border' },
  { prefix: 'border-y', property: 'Border' },
  { prefix: 'ring-offset', property: 'Focus ring offset' },
  { prefix: 'placeholder', property: 'Placeholder' },
  { prefix: 'decoration', property: 'Underline' },
  { prefix: 'divide', property: 'Divider' },
  { prefix: 'outline', property: 'Outline' },
  { prefix: 'shadow', property: 'Shadow' },
  { prefix: 'border', property: 'Border' },
  { prefix: 'accent', property: 'Control accent' },
  { prefix: 'stroke', property: 'Icon stroke' },
  { prefix: 'caret', property: 'Caret' },
  { prefix: 'from', property: 'Gradient' },
  { prefix: 'ring', property: 'Focus ring' },
  { prefix: 'fill', property: 'Icon fill' },
  { prefix: 'text', property: 'Text' },
  { prefix: 'via', property: 'Gradient' },
  { prefix: 'bg', property: 'Background' },
  { prefix: 'to', property: 'Gradient' },
];

/** Variants that depend on a user action, so they are not what is on screen now. */
const STATE_VARIANTS = /^(hover|focus|focus-visible|focus-within|active|disabled|visited|checked|group-hover|group-focus|peer-|aria-|data-)/;

export type Mode = 'light' | 'dark';

export interface TokenHit {
  /** The theme token, e.g. `sem-surface-panel`. */
  token: string;
  /** The class that named it, e.g. `dark:bg-panel`. */
  className: string;
  /** What it paints, e.g. `Background`. */
  property: string;
  variants: string[];
  /** Applies in the mode being previewed, with no state variant in the way. */
  active: boolean;
  /** Needs hover/focus etc. to show up. */
  stateful: boolean;
}

/** Split `dark:hover:bg-panel/40` into its parts, or null if it names no colour. */
export function parseClass(raw: string): Omit<TokenHit, 'active'> | null {
  const parts = raw.split(':');
  const bare = parts[parts.length - 1];
  const variants = parts.slice(0, -1);
  // Alpha modifiers (`bg-panel/40`) change opacity, not which token is read.
  const withoutAlpha = bare.split('/')[0];

  for (const { prefix, property } of UTILITIES) {
    if (!withoutAlpha.startsWith(`${prefix}-`)) continue;
    const scale = withoutAlpha.slice(prefix.length + 1);
    const token = MAP[scale];
    if (!token) continue;
    return {
      token,
      className: raw,
      property,
      variants,
      stateful: variants.some((v) => STATE_VARIANTS.test(v)),
    };
  }
  return null;
}

/**
 * Every token an element's classes name, with the ones actually on screen marked.
 *
 * In dark mode a `dark:` class beats its bare classmate for the same property -
 * which is the whole reason the readout has to know the mode. Reporting both as
 * live would send someone to change the light value of something they are
 * looking at in dark.
 */
export function hitsForClassName(className: string, mode: Mode): TokenHit[] {
  const parsed = className
    .split(/\s+/)
    .filter(Boolean)
    .map(parseClass)
    .filter((h): h is Omit<TokenHit, 'active'> => h !== null);

  const darkWins = new Set(
    parsed
      .filter((h) => h.variants.includes('dark') && !h.stateful)
      .map((h) => h.property),
  );

  return parsed.map((h) => {
    const isDarkClass = h.variants.includes('dark');
    let active = !h.stateful;
    if (isDarkClass) active = active && mode === 'dark';
    // A bare class is overridden in dark mode when a dark: classmate exists.
    else if (mode === 'dark' && darkWins.has(h.property)) active = false;
    return { ...h, active };
  });
}

/**
 * Tokens an element declares directly, for surfaces no class name can describe.
 *
 * The app shell is the reason this exists: the sidebar and top bar take their
 * colours from custom properties in a CSS module, so there is no `bg-…` class to
 * read and the inspector would go silent on the two surfaces people most want to
 * change. Format: `data-theme-token="Background:cmp-sidebar-bg, Text:cmp-sidebar-fg"`.
 */
export function hitsForAttribute(value: string, mode: Mode): TokenHit[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [a, b] = entry.split(':').map((s) => s.trim());
      const token = b ?? a;
      const property = b ? a : 'Color';
      const modeSuffix = /^(light|dark)$/.exec(property.toLowerCase());
      return {
        token,
        className: `data-theme-token="${entry}"`,
        property: modeSuffix ? 'Color' : property,
        variants: [],
        stateful: false,
        active: modeSuffix ? modeSuffix[1] === mode : true,
      };
    });
}

export interface InspectedNode {
  /** `div.rounded.border`, for orienting in the readout. */
  label: string;
  hits: TokenHit[];
  /** Distance from the hovered element; 0 is the element itself. */
  depth: number;
}

const MAX_DEPTH = 6;
const MAX_NODES = 4;

function labelFor(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const named = el.getAttribute('data-theme-part');
  if (named) return named;
  const cls = (el.getAttribute('class') ?? '')
    .split(/\s+/)
    .filter((c) => c && !c.includes(':') && !c.includes('['))
    .slice(0, 2)
    .join('.');
  return cls ? `${tag}.${cls}` : tag;
}

/**
 * Walk from the hovered element outwards, collecting the nodes that name tokens.
 *
 * It climbs because the thing you point at is rarely the thing that is coloured:
 * hovering a cell's text hits a bare `<span>`, and the background you are asking
 * about belongs to the row two levels up.
 */
export function inspectChain(start: Element, root: Element, mode: Mode): InspectedNode[] {
  const out: InspectedNode[] = [];
  let el: Element | null = start;
  let depth = 0;

  while (el && depth <= MAX_DEPTH && out.length < MAX_NODES) {
    const hits = [
      ...hitsForAttribute(el.getAttribute('data-theme-token') ?? '', mode),
      ...hitsForClassName(el.getAttribute('class') ?? '', mode),
    ];
    if (hits.length) out.push({ label: labelFor(el), hits, depth });
    if (el === root) break;
    el = el.parentElement;
    depth += 1;
  }
  return out;
}

export type ControlTab = 'colors' | 'type' | 'layout' | 'components';

export interface ControlLocation {
  /** Which editor tab holds it, or null when nothing in the editor moves it. */
  tab: ControlTab | null;
  /** Sentence for the readout. */
  note: string;
}

/** Where in the editor a token is changed - or why it cannot be. */
export function controlFor(token: string): ControlLocation {
  if (token.startsWith('cmp-')) {
    return { tab: 'components', note: 'Components tab' };
  }
  if (token.startsWith('sem-')) {
    return { tab: 'colors', note: 'Colors tab' };
  }
  if (token.startsWith('radius-') || token.startsWith('shadow-')) {
    return { tab: 'layout', note: 'Layout tab' };
  }
  if (token.startsWith('fw-') || token.startsWith('font-')) {
    return { tab: 'type', note: 'Typography tab' };
  }
  if (token.startsWith('theme-')) {
    // The pre-v2 palette variables, still driving a few surfaces.
    return { tab: 'colors', note: 'Follows the base palette, not a single control' };
  }
  if (token.startsWith('c-')) {
    const ramp = token.slice(2).replace(/-\d+$/, '');
    return {
      tab: null,
      note: `Not tokenised - follows the ${ramp} ramp directly, so no control changes it on its own`,
    };
  }
  return { tab: null, note: 'No editor control' };
}

/** The one line worth showing when space is tight: the live background or text. */
export function primaryHit(nodes: InspectedNode[]): TokenHit | null {
  for (const node of nodes) {
    const bg = node.hits.find((h) => h.active && h.property === 'Background');
    if (bg) return bg;
  }
  for (const node of nodes) {
    const any = node.hits.find((h) => h.active);
    if (any) return any;
  }
  return null;
}
